import {
  createContext,
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react'
import {
  isTabletTouchZone,
  readLayoutPreference,
  resolveIsMobile,
  writeLayoutPreference,
} from '../consts/layout-mode'

const defaultContextValue = {
  isMobile: false,
  isTabletTouchZone: false,
  layoutPreference: 'desktop',
  setLayoutPreference: () => {},
}

const LayoutModeContext = createContext(defaultContextValue)

export function LayoutModeProvider({ children }) {
  const [viewportWidth, setViewportWidth] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth : 0,
  )
  const [layoutPreference, setLayoutPreferenceState] = useState(readLayoutPreference)

  useLayoutEffect(() => {
    const handleResize = () => setViewportWidth(window.innerWidth)
    handleResize()
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  const setLayoutPreference = useCallback((preference) => {
    setLayoutPreferenceState(preference)
    writeLayoutPreference(preference)
  }, [])

  const inTabletTouchZone = isTabletTouchZone(viewportWidth)

  const isMobile = resolveIsMobile({
    width: viewportWidth,
    layoutPreference,
  })

  const value = useMemo(
    () => ({
      isMobile,
      isTabletTouchZone: inTabletTouchZone,
      layoutPreference,
      setLayoutPreference,
    }),
    [isMobile, inTabletTouchZone, layoutPreference, setLayoutPreference],
  )

  return (
    <LayoutModeContext.Provider value={value}>
      {children}
    </LayoutModeContext.Provider>
  )
}

export function useLayoutMode() {
  return useContext(LayoutModeContext)
}
